import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/App';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Send, Mail, Inbox, Paperclip, Download, X } from 'lucide-react';
import { toast } from 'sonner';

const Messages = ({ user, onLogout }) => {
  const location = useLocation();
  const [messages, setMessages] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [newMessage, setNewMessage] = useState({
    to_user_id: location.state?.toUserId || '',
    subject: '',
    content: ''
  });
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    fetchMessages();
    fetchOrganizations();
  }, []);

  useEffect(() => {
    if (location.state?.toUserId) {
      setNewMessage(prev => ({ ...prev, to_user_id: location.state.toUserId }));
      setDialogOpen(true);
    }
  }, [location.state]);

  const fetchMessages = async () => {
    try {
      const response = await api.get('/messages');
      setMessages(response.data);
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoading(false);
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await api.get('/organizations');
      setOrganizations(response.data.filter(org => org.user_id !== user.id));
    } catch (error) {
      console.error('Failed to load organizations');
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('to_user_id', newMessage.to_user_id);
      formData.append('subject', newMessage.subject);
      formData.append('content', newMessage.content);
      
      selectedFiles.forEach((file) => {
        formData.append('files', file);
      });

      await api.post('/messages', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      toast.success('Message sent successfully!');
      setNewMessage({ to_user_id: '', subject: '', content: '' });
      setSelectedFiles([]);
      setDialogOpen(false);
      fetchMessages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    }
  };

  const downloadAttachment = async (fileId, filename) => {
    try {
      const response = await api.get(`/messages/download/${fileId}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const handleViewMessage = async (message) => {
    setSelectedMessage(message);
    if (message.to_user_id === user.id && !message.read) {
      try {
        await api.put(`/messages/${message.id}/read`);
        fetchMessages();
      } catch (error) {
        console.error('Failed to mark message as read');
      }
    }
  };

  const inbox = messages.filter(m => m.to_user_id === user.id);
  const sent = messages.filter(m => m.from_user_id === user.id);
  const unreadCount = inbox.filter(m => !m.read).length;

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="messages-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Messages</h1>
            <p className="text-gray-600">Communicate with other organizations</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-green-600 hover:bg-green-700 text-white" data-testid="compose-message-button">
                <Send className="h-4 w-4 mr-2" />
                Compose Message
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[600px]">
              <DialogHeader>
                <DialogTitle>New Message</DialogTitle>
                <DialogDescription>Send a message to another organization</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSendMessage} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="recipient">Recipient</Label>
                  <Select
                    value={newMessage.to_user_id}
                    onValueChange={(value) => setNewMessage({ ...newMessage, to_user_id: value })}
                    required
                  >
                    <SelectTrigger data-testid="message-recipient-select">
                      <SelectValue placeholder="Select an organization" />
                    </SelectTrigger>
                    <SelectContent>
                      {organizations.map((org) => (
                        <SelectItem key={org.user_id} value={org.user_id}>
                          {org.org_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    placeholder="Message subject"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                    required
                    data-testid="message-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="content">Message</Label>
                  <Textarea
                    id="content"
                    placeholder="Type your message here..."
                    value={newMessage.content}
                    onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                    rows={6}
                    required
                    data-testid="message-content-input"
                  />
                </div>
                
                {/* File Attachments */}
                <div className="space-y-2">
                  <Label htmlFor="attachments">Attachments (Optional)</Label>
                  <div className="flex gap-2">
                    <Input
                      id="attachments"
                      type="file"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => document.getElementById('attachments').click()}
                      className="w-full"
                    >
                      <Paperclip className="h-4 w-4 mr-2" />
                      Attach Files
                    </Button>
                  </div>
                  {selectedFiles.length > 0 && (
                    <div className="space-y-2 mt-2">
                      {selectedFiles.map((file, index) => (
                        <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                          <div className="flex items-center gap-2 flex-1 min-w-0">
                            <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0" />
                            <span className="text-sm text-gray-700 truncate">{file.name}</span>
                            <span className="text-xs text-gray-500 flex-shrink-0">
                              ({(file.size / 1024).toFixed(1)} KB)
                            </span>
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => removeFile(index)}
                            className="h-6 w-6 p-0"
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700 text-white" data-testid="send-message-submit">
                    <Send className="h-4 w-4 mr-2" />
                    Send Message
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* Message List */}
          <div className="lg:col-span-1 space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Inbox className="h-5 w-5" />
                    Inbox
                  </span>
                  {unreadCount > 0 && (
                    <Badge className="bg-red-500">{unreadCount}</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[500px] overflow-y-auto">
                {loading ? (
                  <p className="text-gray-500 text-sm">Loading...</p>
                ) : inbox.length > 0 ? (
                  inbox.map((message) => (
                    <button
                      key={message.id}
                      onClick={() => handleViewMessage(message)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedMessage?.id === message.id
                          ? 'bg-green-50 border-2 border-green-200'
                          : 'hover:bg-gray-50 border border-gray-200'
                      } ${!message.read ? 'font-semibold' : ''}`}
                      data-testid={`message-item-${message.id}`}
                    >
                      <div className="flex items-start justify-between mb-1">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {message.from_org_name}
                        </p>
                        {!message.read && (
                          <span className="w-2 h-2 bg-green-600 rounded-full flex-shrink-0 mt-1.5"></span>
                        )}
                      </div>
                      <p className="text-xs text-gray-600 truncate">{message.subject}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(message.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm text-center py-8">No messages</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="h-5 w-5" />
                  Sent
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 max-h-[300px] overflow-y-auto">
                {sent.length > 0 ? (
                  sent.map((message) => (
                    <button
                      key={message.id}
                      onClick={() => handleViewMessage(message)}
                      className={`w-full text-left p-3 rounded-lg transition-colors ${
                        selectedMessage?.id === message.id
                          ? 'bg-green-50 border-2 border-green-200'
                          : 'hover:bg-gray-50 border border-gray-200'
                      }`}
                      data-testid={`sent-message-item-${message.id}`}
                    >
                      <p className="text-sm font-medium text-gray-900 truncate">
                        To: {message.to_org_name}
                      </p>
                      <p className="text-xs text-gray-600 truncate">{message.subject}</p>
                      <p className="text-xs text-gray-400 mt-1">
                        {new Date(message.created_at).toLocaleDateString()}
                      </p>
                    </button>
                  ))
                ) : (
                  <p className="text-gray-500 text-sm text-center py-8">No sent messages</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Message Detail */}
          <div className="lg:col-span-2">
            {selectedMessage ? (
              <Card>
                <CardHeader>
                  <div className="space-y-2">
                    <CardTitle className="text-2xl">{selectedMessage.subject}</CardTitle>
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <span className="font-medium">
                        {selectedMessage.from_user_id === user.id ? 'To' : 'From'}:
                      </span>
                      <span>
                        {selectedMessage.from_user_id === user.id
                          ? selectedMessage.to_org_name
                          : selectedMessage.from_org_name}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">
                      {new Date(selectedMessage.created_at).toLocaleString()}
                    </p>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="prose max-w-none mb-4">
                    <p className="whitespace-pre-wrap text-gray-700">{selectedMessage.content}</p>
                  </div>
                  
                  {/* Attachments */}
                  {selectedMessage.attachments && selectedMessage.attachments.length > 0 && (
                    <div className="border-t pt-4 mt-4">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Attachments ({selectedMessage.attachments.length})
                      </p>
                      <div className="space-y-2">
                        {selectedMessage.attachments.map((attachment, index) => (
                          <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                            <div className="flex items-center gap-2 flex-1 min-w-0">
                              <Paperclip className="h-4 w-4 text-gray-500 flex-shrink-0" />
                              <span className="text-sm text-gray-700 truncate">{attachment.filename}</span>
                              <span className="text-xs text-gray-500 flex-shrink-0">
                                ({(attachment.size / 1024).toFixed(1)} KB)
                              </span>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadAttachment(attachment.file_id, attachment.filename)}
                              className="flex-shrink-0"
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ) : (
              <Card className="h-full flex items-center justify-center">
                <CardContent className="text-center py-12">
                  <MessageSquare className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-500">Select a message to view</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Messages;